import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parse, isValid } from "date-fns";
import { api } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarIcon, Globe, ShieldAlert } from "lucide-react";
import { COUNTRIES } from "@/lib/countries";

const SKIP_KEY = "cheerup_profile_prompt_skips";
const MAX_SKIPS = 3;

export default function ProfilePromptDialog() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [checked, setChecked] = useState(false); // has async check completed
    const [profile, setProfile] = useState(null);
    const [dob, setDob] = useState("");
    const [country, setCountry] = useState("");
    const [dateOpen, setDateOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [skips, setSkips] = useState(() => parseInt(localStorage.getItem(SKIP_KEY) || "0", 10) || 0);

    const dobDate = dob ? parse(dob, "yyyy-MM-dd", new Date()) : null;
    const remainingSkips = Math.max(0, MAX_SKIPS - skips);
    const mustFill = remainingSkips === 0;

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/profile");
                setProfile(data);
                setDob(data.dob || "");
                setCountry(data.country || "");
                if (!data.dob || !data.country) {
                    setOpen(true);
                }
            } catch {
                // silent — user might not be authed yet
            } finally {
                setChecked(true);
            }
        })();
    }, []);

    const save = async () => {
        if (!dob) {
            toast.error("Please choose your date of birth");
            return;
        }
        if (!country) {
            toast.error("Please choose your country / region");
            return;
        }
        setSaving(true);
        try {
            await api.patch("/profile", { dob, country });
            localStorage.removeItem(SKIP_KEY);
            setOpen(false);
            toast.success("Thanks! Your profile is complete.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const handleSkip = () => {
        if (mustFill) return;
        const next = skips + 1;
        localStorage.setItem(SKIP_KEY, String(next));
        setSkips(next);
        setOpen(false);
    };

    const goToProfile = () => {
        setOpen(false);
        navigate("/app/profile");
    };

    if (!checked) return null;

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                // Prevent dismissing via ESC / outside click when the user is out of skips
                if (!o && mustFill) return;
                setOpen(o);
            }}
        >
            <DialogContent
                className="rounded-3xl max-w-lg"
                onEscapeKeyDown={(e) => { if (mustFill) e.preventDefault(); }}
                onPointerDownOutside={(e) => { if (mustFill) e.preventDefault(); }}
                onInteractOutside={(e) => { if (mustFill) e.preventDefault(); }}
                data-testid="profile-prompt-dialog"
            >
                <DialogHeader>
                    <div className="flex items-center gap-2 mb-2">
                        <ShieldAlert className={`w-5 h-5 ${mustFill ? "text-destructive" : "text-primary"}`} strokeWidth={1.5} />
                        <DialogTitle className="font-heading text-2xl font-[800] tracking-tight">
                            {mustFill ? "Please complete your profile" : "Just a couple of quick details"}
                        </DialogTitle>
                    </div>
                    <DialogDescription className="leading-relaxed">
                        We ask for your <strong>date of birth</strong> and <strong>country/region</strong> to keep CheerUp! safe, age-appropriate, and gently tailored to you. This takes 15 seconds.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 pt-2">
                    <div>
                        <Label className="text-sm font-medium mb-2 block">Date of birth</Label>
                        <Popover open={dateOpen} onOpenChange={setDateOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button" variant="outline"
                                    className="w-full h-12 rounded-2xl bg-background justify-start font-normal border-border"
                                    data-testid="prompt-dob-trigger"
                                >
                                    <CalendarIcon className="w-4 h-4 mr-2 text-muted-foreground" strokeWidth={1.5} />
                                    {dobDate && isValid(dobDate) ? format(dobDate, "PPP") : "Choose your date of birth"}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 rounded-2xl" align="start">
                                <Calendar
                                    mode="single"
                                    selected={dobDate || undefined}
                                    onSelect={(d) => {
                                        if (d) { setDob(format(d, "yyyy-MM-dd")); setDateOpen(false); }
                                    }}
                                    captionLayout="dropdown-buttons"
                                    fromYear={1900}
                                    toYear={new Date().getFullYear()}
                                    defaultMonth={dobDate || new Date(2000, 0)}
                                    disabled={(d) => d > new Date() || d < new Date("1900-01-01")}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    <div>
                        <Label className="text-sm font-medium mb-2 block">Country / Region</Label>
                        <Select value={country || "__unset__"} onValueChange={(v) => setCountry(v === "__unset__" ? "" : v)}>
                            <SelectTrigger className="rounded-2xl bg-background h-12" data-testid="prompt-country-select">
                                <SelectValue placeholder="Choose your country">
                                    {country ? (COUNTRIES.find(c => c.code === country)?.name || country) : (
                                        <span className="flex items-center gap-2 text-muted-foreground">
                                            <Globe className="w-4 h-4" strokeWidth={1.5} /> Choose your country
                                        </span>
                                    )}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="rounded-2xl max-h-64">
                                <SelectItem value="__unset__" className="hidden">— select —</SelectItem>
                                {COUNTRIES.map(c => (
                                    <SelectItem key={c.code} value={c.code} data-testid={`prompt-country-${c.code}`}>{c.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {mustFill ? (
                        <p className="text-xs text-destructive font-medium" data-testid="prompt-must-fill-note">
                            You've skipped this a few times already — we now need these details to continue using CheerUp!.
                        </p>
                    ) : (
                        <p className="text-xs text-muted-foreground" data-testid="prompt-skips-note">
                            You can skip for now — {remainingSkips} skip{remainingSkips === 1 ? "" : "s"} left before we'll need to ask again.
                        </p>
                    )}
                </div>

                <DialogFooter className="mt-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-between sm:items-center">
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        {!mustFill && (
                            <Button
                                variant="ghost"
                                onClick={handleSkip}
                                className="rounded-full"
                                data-testid="prompt-skip-btn"
                            >
                                Skip for now
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            onClick={goToProfile}
                            className="rounded-full bg-background"
                            data-testid="prompt-goto-profile-btn"
                        >
                            Go to Profile
                        </Button>
                    </div>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="rounded-full h-11 px-6 font-medium shadow-soft w-full sm:w-auto"
                        data-testid="prompt-save-btn"
                    >
                        {saving ? "Saving…" : "Save & continue"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
